/**
 * Header + centered message shell used while a Candid document resolves.
 *
 * Mirrors the {@link CandidRpaAuthLetterPdfPanel} layout so the panel does
 * not shift once the real preview renders.
 *
 * @module candidRpaDocumentPreviewStatus
 */

/** Props for {@link CandidRpaDocumentPreviewStatus}. */
export interface CandidRpaDocumentPreviewStatusProps {
  /** Document label shown in the panel header. */
  name?: string;
  /** Timestamp label shown in the panel header. */
  time?: string;
  /** Message shown in the panel body. */
  message: string;
  /** Whether to show the spinner above the message. */
  isLoading?: boolean;
  /** Whether to style the message as a failure. */
  isError?: boolean;
}

/**
 * Renders the loading or error state for a document preview tab.
 *
 * @param props.name - Header document name
 * @param props.time - Header timestamp label
 * @param props.message - Body message
 * @param props.isLoading - Show spinner when true
 * @param props.isError - Style the message as an error when true
 * @returns Status panel matching the preview panel layout
 */
const CandidRpaDocumentPreviewStatus = ({
  name = "Name",
  time = "Time",
  message,
  isLoading = false,
  isError = false,
}: CandidRpaDocumentPreviewStatusProps) => {
  return (
    <div className="document-preview-status flex h-full flex-col overflow-hidden">
      <div className="document-preview-status-header flex items-center justify-between border-b border-primaryGray-14 px-4 py-2 text-tiny font-semibold">
        <div className="document-preview-status-name">{name}</div>
        <div className="document-preview-status-time">{time}</div>
      </div>
      <div className="document-preview-status-content flex min-h-0 flex-1 items-center justify-center">
        <div className="text-center">
          {isLoading && (
            <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-gray-900"></div>
          )}
          <div
            className={`text-sm ${isError ? "text-red-600" : "text-gray-500"}`}
          >
            {message}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidRpaDocumentPreviewStatus;
